import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import "./CapacitacionesTimeline.css";

const API = process.env.REACT_APP_API_URL;
const token = sessionStorage.getItem("token");

const ESTADOS = ["Todos", "Programada", "En proceso", "Finalizada"];

const CapacitacionesTimeline = ({
    capacitaciones = [],
    empleadosCapacitacion = [],
    empleados = [],
    puestos = [],
    documentos = [],
}) => {
    const [temasOpen, setTemasOpen] = useState({});
    const [capsOpen, setCapsOpen] = useState({});
    const [capacitacionTemas, setCapacitacionTemas] = useState([]);
    const [busqueda, setBusqueda] = useState("");
    const [filtroEstado, setFiltroEstado] = useState("Todos");

    const obtenerCapacitacionTemas = async () => {
        try {
            const res = await axios.get(`${API}/capacitacion-temas/`, {
                headers: { Authorization: `Token ${token}` },
            });
            setCapacitacionTemas(res.data);
        } catch (error) {
            console.error("Error obteniendo relaciones:", error.response?.data || error);
        }
    };

    useEffect(() => {
        obtenerCapacitacionTemas();
    }, []);

    const obtenerEstado = (cap) => {
        const hoy = new Date();
        const inicio = new Date(cap.fechainicio);
        const fin = new Date(cap.fechafin);
        if (hoy >= inicio && hoy <= fin) return "En proceso";
        if (hoy > fin) return "Finalizada";
        return "Programada";
    };

    const busquedaLower = busqueda.toLowerCase().trim();

    const estructura = useMemo(() => {
        const grouped = {};

        capacitacionTemas.forEach((rel) => {
            const cap = capacitaciones.find(
                (c) => Number(c.idcapacitacion) === Number(rel.idcapacitacion)
            );
            if (!cap) return;

            const tema = rel.nombretema || "Sin tema";

            // Filtro por búsqueda: coincide en nombre de capacitación O en nombre de tema
            if (busquedaLower !== "") {
                const matchCap = cap.nombreevento?.toLowerCase().includes(busquedaLower);
                const matchTema = tema.toLowerCase().includes(busquedaLower);
                if (!matchCap && !matchTema) return;
            }

            // Filtro por estado
            if (filtroEstado !== "Todos" && obtenerEstado(cap) !== filtroEstado) return;

            if (!grouped[tema]) grouped[tema] = [];

            const yaExiste = grouped[tema].some(
                (c) => Number(c.idcapacitacion) === Number(cap.idcapacitacion)
            );
            if (!yaExiste) grouped[tema].push(cap);
        });

        return grouped;
    }, [capacitaciones, capacitacionTemas, busquedaLower, filtroEstado]);

    // Auto-expandir temas cuando hay búsqueda activa
    useEffect(() => {
        if (busquedaLower !== "") {
            const abiertos = {};
            Object.keys(estructura).forEach((tema) => { abiertos[tema] = true; });
            setTemasOpen(abiertos);
        }
    }, [busquedaLower, estructura]);

    const totalTemas = Object.keys(estructura).length;

    const totalCapacitaciones = useMemo(() => {
        const ids = new Set();
        Object.values(estructura).forEach((caps) =>
            caps.forEach((c) => ids.add(c.idcapacitacion))
        );
        return ids.size;
    }, [estructura]);

    const toggleTema = (tema) => {
        setTemasOpen((prev) => ({ ...prev, [tema]: !prev[tema] }));
    };

    const toggleCap = (id) => {
        setCapsOpen((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const formatFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString("es-GT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const obtenerEstadisticas = (idCapacitacion) => {
        const registros = empleadosCapacitacion.filter(
            (emp) => Number(emp.idcapacitacion) === Number(idCapacitacion)
        );
        const total = registros.length;
        const asistentes = registros.filter(
            (r) => r.asistencia?.toLowerCase() === "sí"
        ).length;
        const ausentes = total - asistentes;
        const porcentaje = total > 0 ? Math.round((asistentes / total) * 100) : 0;
        return { total, asistentes, ausentes, porcentaje };
    };

    const obtenerParticipantes = (idCapacitacion) => {
        const registros = empleadosCapacitacion.filter(
            (emp) => Number(emp.idcapacitacion) === Number(idCapacitacion)
        );
        return registros.map((registro) => {
            const empleado = empleados.find(
                (emp) => Number(emp.idempleado) === Number(registro.idempleado)
            );
            const puesto = puestos.find(
                (p) => Number(p.idpuesto) === Number(empleado?.idpuesto)
            );
            const documento = (documentos || []).find(
                (doc) => Number(doc.iddocumento) === Number(registro.iddocumento)
            );
            return {
                ...registro,
                nombreCompleto: empleado
                    ? `${empleado.nombre || ""} ${empleado.apellido || ""}`.replace(/\s+/g, " ").trim()
                    : "Empleado no encontrado",
                email: empleado?.email || "",
                dpi: empleado?.dpi || "",
                genero: empleado?.genero || "",
                telefono: empleado?.telefonocelular || "",
                puesto: puesto?.nombrepuesto || puesto?.nombre || "Sin puesto",
                documento,
            };
        });
    };

    // Resalta el texto que coincide con la búsqueda
    const resaltar = (texto) => {
        if (!busquedaLower || !texto) return texto;
        const idx = texto.toLowerCase().indexOf(busquedaLower);
        if (idx === -1) return texto;
        return (
            <>
                {texto.slice(0, idx)}
                <mark className="timeline-highlight">
                    {texto.slice(idx, idx + busquedaLower.length)}
                </mark>
                {texto.slice(idx + busquedaLower.length)}
            </>
        );
    };

    const estadoClass = {
        "Programada": "status-programada",
        "En proceso": "status-en-proceso",
        "Finalizada": "status-finalizada",
    };

    return (
        <div className="timeline-container">

            {/* ENCABEZADO */}
            <div className="timeline-header">
                <div className="timeline-header-title">
                    <h2 className="timeline-title">Registro de Capacitaciones</h2>
                    <div className="timeline-counters">
                        <span className="timeline-counter">
                            <strong>{totalTemas}</strong> tema{totalTemas !== 1 ? "s" : ""}
                        </span>
                        <span className="timeline-counter-divider">·</span>
                        <span className="timeline-counter">
                            <strong>{totalCapacitaciones}</strong> capacitacion{totalCapacitaciones !== 1 ? "es" : ""}
                        </span>
                    </div>
                </div>

                {/* FILTROS */}
                <div className="timeline-controls">
                    <div className="timeline-search-wrapper">
                        <svg className="timeline-search-icon" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        <input
                            type="text"
                            className="timeline-search"
                            placeholder="Buscar por tema o capacitación..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                        {busqueda && (
                            <button className="timeline-search-clear" onClick={() => setBusqueda("")}>
                                ×
                            </button>
                        )}
                    </div>

                    <div className="timeline-filter-buttons">
                        {ESTADOS.map((estado) => (
                            <button
                                key={estado}
                                className={`timeline-filter-btn ${filtroEstado === estado ? "active" : ""} ${estado !== "Todos" ? estadoClass[estado] : ""}`}
                                onClick={() => setFiltroEstado(estado)}
                            >
                                {estado}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* TEMAS */}
            <div className="timeline-years-scroll">
                {Object.keys(estructura).length === 0 ? (
                    <div className="timeline-empty">
                        No se encontraron capacitaciones con los filtros aplicados.
                    </div>
                ) : (
                    Object.keys(estructura)
                        .sort()
                        .map((tema) => {
                            const abiertoTema = temasOpen[tema];
                            const capsDelTema = estructura[tema];

                            return (
                                <div key={tema} className="timeline-year-block">
                                    <button
                                        className="timeline-year-button"
                                        onClick={() => toggleTema(tema)}
                                    >
                                        <div className="timeline-year-button-left">
                                            <span className="timeline-tema-arrow">
                                                {abiertoTema ? "▾" : "▸"}
                                            </span>
                                            <span className="timeline-tema-nombre">
                                                {resaltar(tema)}
                                            </span>
                                        </div>
                                        <span className="timeline-tema-badge">
                                            {capsDelTema.length} capacitacion{capsDelTema.length !== 1 ? "es" : ""}
                                        </span>
                                    </button>

                                    {abiertoTema && (
                                        <div className="timeline-events timeline-events-scroll">
                                            {capsDelTema.map((cap) => {
                                                const estado = obtenerEstado(cap);
                                                const stats = obtenerEstadisticas(cap.idcapacitacion || cap.id);
                                                const capId = cap.idcapacitacion || cap.id;
                                                const abiertoCap = capsOpen[capId];

                                                return (
                                                    <div key={capId} className="timeline-card">
                                                        <div className="timeline-dot"></div>
                                                        <div className="timeline-card-content">
                                                            <button
                                                                className="timeline-card-toggle"
                                                                onClick={() => toggleCap(capId)}
                                                            >
                                                                <div className="timeline-card-header">
                                                                    <div className="timeline-card-header-left">
                                                                        <span className="timeline-card-arrow">
                                                                            {abiertoCap ? "▾" : "▸"}
                                                                        </span>
                                                                        <h3>{resaltar(cap.nombreevento)}</h3>
                                                                    </div>
                                                                    <span className={`timeline-status ${estadoClass[estado]}`}>
                                                                        {estado}
                                                                    </span>
                                                                </div>
                                                            </button>

                                                            {abiertoCap && (
                                                                <>
                                                                    <div className="timeline-card-meta">
                                                                        <p><strong>Institución:</strong> {cap.institucionfacilitadora}</p>
                                                                        <p><strong>Lugar:</strong> {cap.lugar}</p>
                                                                        <p>
                                                                            <strong>Fecha:</strong>{" "}
                                                                            {formatFecha(cap.fechainicio)} — {formatFecha(cap.fechafin)}
                                                                        </p>
                                                                        <p><strong>Monto:</strong> Q.{cap.montoejecutado}</p>
                                                                    </div>

                                                                    <div className="timeline-stats">
                                                                        <div className="timeline-stat-item">
                                                                            <span className="timeline-stat-value">{stats.total}</span>
                                                                            <span className="timeline-stat-label">Participantes</span>
                                                                        </div>
                                                                        <div className="timeline-stat-item">
                                                                            <span className="timeline-stat-value stat-asistio">{stats.asistentes}</span>
                                                                            <span className="timeline-stat-label">Asistieron</span>
                                                                        </div>
                                                                        <div className="timeline-stat-item">
                                                                            <span className="timeline-stat-value stat-ausente">{stats.ausentes}</span>
                                                                            <span className="timeline-stat-label">Ausentes</span>
                                                                        </div>
                                                                        <div className="timeline-stat-item">
                                                                            <span className="timeline-stat-value">{stats.porcentaje}%</span>
                                                                            <span className="timeline-stat-label">Asistencia</span>
                                                                        </div>
                                                                    </div>

                                                                    <div className="timeline-participants">
                                                                        <h4>Participantes</h4>
                                                                        <div className="timeline-participants-list">
                                                                            {obtenerParticipantes(capId).map((participante) => {
                                                                                const asistio = participante.asistencia?.toLowerCase() === "sí";
                                                                                const archivoUrl = participante.documento
                                                                                    ? (participante.documento.archivo || participante.documento.archivo_url)
                                                                                    : null;

                                                                                return (
                                                                                    <div
                                                                                        key={participante.idempleadocapacitacion}
                                                                                        className={`timeline-participant ${asistio ? "asistio" : "ausente"}`}
                                                                                    >
                                                                                        <div className="timeline-participant-info">
                                                                                            <div className="timeline-participant-data">
                                                                                                <span className="timeline-participant-name">
                                                                                                    {participante.nombreCompleto}
                                                                                                </span>
                                                                                                <span className="timeline-participant-position">
                                                                                                    {participante.puesto}
                                                                                                </span>
                                                                                                <span className="timeline-participant-email">
                                                                                                    {participante.email || "Sin correo"}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>

                                                                                        <div className="timeline-participant-status">
                                                                                            {asistio ? "Asistió" : "Ausente"}
                                                                                        </div>

                                                                                        <div className="timeline-document-wrapper">
                                                                                            {archivoUrl ? (
                                                                                                <a
                                                                                                    href={archivoUrl}
                                                                                                    target="_blank"
                                                                                                    rel="noreferrer"
                                                                                                    className="timeline-document delivered"
                                                                                                >
                                                                                                    Ver informe
                                                                                                </a>
                                                                                            ) : (
                                                                                                <div className="timeline-document pending">
                                                                                                    Sin informe
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                )}
            </div>
        </div>
    );
};

export default CapacitacionesTimeline;